import type { Dish } from '../types';

/** The only categories exposed by the frontend, in kitchen workflow order. */
export const DISH_CATEGORY_ORDER = [
  '热菜',
  '凉菜',
  '汤菜',
  '早餐',
  '其他',
] as const;

export const FALLBACK_DISH_CATEGORIES = DISH_CATEGORY_ORDER;

type DishCategory = (typeof DISH_CATEGORY_ORDER)[number];

const LEGACY_CATEGORY_MAP = new Map<string, DishCategory>([
  ['热菜', '热菜'],
  ['荤菜', '热菜'],
  ['半荤', '热菜'],
  ['素菜', '热菜'],
  ['凉菜', '凉菜'],
  ['汤菜', '汤菜'],
  ['汤羹', '汤菜'],
  ['汤羹炖品', '汤菜'],
  ['早餐', '早餐'],
  ['主食', '早餐'],
  ['主食早餐', '早餐'],
  ['其他', '其他'],
  ['调味品补货', '其他'],
]);

const COLD_PATTERN = /凉|拌|白砍|冷吃/;
const SOUP_PATTERN = /汤|羹|炖|煲/;
const BREAKFAST_PATTERN = /早餐|饭|面|粉|粥|馒头|包子|饺子|米线|抄手|发糕|油条|豆浆/;

function searchableText(dish: Dish): string {
  return `${dish.name} ${dish.ingredients.map((ingredient) => ingredient.name).join(' ')}`;
}

/**
 * Prefer an explicit future API category, while keeping old dish data useful.
 * The fallback is intentionally deterministic so filters never reshuffle between renders.
 */
export function dishCategory(dish: Dish): string {
  if (dish.type === 'condiment_restock') return '其他';
  if (dish.category !== undefined) return LEGACY_CATEGORY_MAP.get(dish.category.trim()) ?? '其他';
  const text = searchableText(dish);
  if (COLD_PATTERN.test(text)) return '凉菜';
  if (SOUP_PATTERN.test(text)) return '汤菜';
  if (BREAKFAST_PATTERN.test(text)) return '早餐';
  return '热菜';
}

export function dishCategories(dishes: Dish[]): string[] {
  const categories = new Set(dishes.map(dishCategory));
  return DISH_CATEGORY_ORDER.filter((category) => categories.has(category));
}

export function matchesDishSearch(dish: Dish, query: string): boolean {
  const normalized = query.trim().toLocaleLowerCase('zh-CN');
  if (!normalized) return true;
  return `${searchableText(dish)} ${dishCategory(dish)}`.toLocaleLowerCase('zh-CN').includes(normalized);
}
